import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import Loading from 'components/shared/Utils/Loading';
import IconPillActive from 'assets/icon-em-pill-active.svg';
import IconPillType from 'assets/icon-em-pill-type.svg';
import IconPillClock from 'assets/icon-em-pill-clock.svg';
import IconPillDelivered from 'assets/icon-em-pill-delivered.svg';
import {
  GET_EMAIL_MARKETING_CAMPAIGN_DETAIL,
  GET_EMAIL_MARKETING_CAMPAIGN_PREVIEW,
  CLEAR_EMAIL_MARKETING_CAMPAIGN_DETAIL
} from 'store/emailMarketing/action';
import {
  CAMPAIGN_STATUS_LABELS,
  CAMPAIGN_PREVIEW_UNAVAILABLE,
  CAMPAIGN_PREVIEW_ERROR,
  EMPTY_VALUE,
  campaignTitle,
  formatCount,
  formatRate,
  formatRateShort,
  formatCampaignDate
} from 'helpers/constants/emailMarketing';
import {
  CampaignBackLink,
  CampaignDetailCard,
  CampaignDetailTitle,
  CampaignDetailPills,
  CampaignDetailPill,
  CampaignTiles,
  CampaignTile,
  CampaignTileValue,
  CampaignTileName,
  CampaignDetailColumns,
  CampaignSectionTitle,
  CampaignFunnel,
  CampaignFunnelStage,
  CampaignFunnelCount,
  CampaignFunnelTrack,
  CampaignFunnelFill,
  CampaignFunnelLabel,
  CampaignPreviewFrame,
  CampaignPreviewEmpty,
  CampaignsEmpty,
  CampaignDetailRow,
  CampaignDetailLabel,
  CampaignDetailValue,
  EmailMarketingSpinner
} from 'routes/App/emailMarketingV2/components/styles';

const FUNNEL_LABELS = {
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  ordered: 'Ordered'
};

/* Appended, not prepended, so the email keeps its own doctype. */
const PREVIEW_DISABLE_LINKS =
  '<style>a,area,button,input,select,textarea{pointer-events:none!important;cursor:default!important}</style>';

/* Cancelling click and auxclick covers every activation path, including the
   programmatic one a screen reader dispatches and a middle-click on an image-map
   <area>, which pointer-events does not reach. The href stays, so the email
   keeps its link styling and nothing leaves the accessibility tree. */
const cancelActivation = (activation) => activation.preventDefault();

const makePreviewReadOnly = (event) => {
  try {
    const doc = event.target?.contentDocument;
    if (!doc) return;
    doc.addEventListener('click', cancelActivation, true);
    doc.addEventListener('auxclick', cancelActivation, true);
    doc
      .querySelectorAll('a, area, button, input, select, textarea')
      .forEach((el) => el.setAttribute('tabindex', '-1'));
  } catch {
    /* the stylesheet still covers pointer interaction */
  }
};

const CampaignDetail = ({campaignId, sourceType, onBack}) => {
  const dispatch = useDispatch();
  const {
    campaignDetail,
    campaignDetailSpinner,
    campaignPreview,
    campaignPreviewSpinner,
    campaignPreviewError
  } = useSelector((state) => state.emailMarketingReducer);
  const {locationId} = useSelector((state) => state.user);

  useEffect(() => {
    if (locationId && campaignId) {
      dispatch(GET_EMAIL_MARKETING_CAMPAIGN_DETAIL({campaignId, sourceType}));
      dispatch(GET_EMAIL_MARKETING_CAMPAIGN_PREVIEW({campaignId, sourceType}));
    }
    return () => dispatch(CLEAR_EMAIL_MARKETING_CAMPAIGN_DETAIL());
  }, [locationId, campaignId, sourceType]);

  const backLink = (
    <CampaignBackLink type='button' onClick={onBack}>
      &larr; Back to all Email Marketing
    </CampaignBackLink>
  );

  if (campaignDetailSpinner) {
    return (
      <>
        {backLink}
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      </>
    );
  }

  if (!campaignDetail) {
    return (
      <>
        {backLink}
        <CampaignsEmpty>
          <span>We could not load this campaign. Please try again.</span>
        </CampaignsEmpty>
      </>
    );
  }

  const {name, tiles, details, deliveryFunnel, audience} = campaignDetail;
  const statusLabel = CAMPAIGN_STATUS_LABELS[details?.statusLabel] || '';
  const isActive = details?.statusLabel === 'active';
  const typeLabel = details?.type === 'automated' ? 'Automated' : 'Single Send';

  const funnel = deliveryFunnel || [];
  const funnelMax = funnel.reduce(
    (max, {count}) => Math.max(max, count || 0),
    0
  );
  const clicks = funnel.find(({stage}) => stage === 'clicked')?.count;

  const renderPreview = () => {
    if (campaignPreviewSpinner) {
      return (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      );
    }

    if (!campaignPreview?.preview?.html) {
      const copy = campaignPreviewError
        ? CAMPAIGN_PREVIEW_ERROR
        : CAMPAIGN_PREVIEW_UNAVAILABLE;
      return (
        <CampaignPreviewEmpty>
          <strong>{copy.title}</strong>
          <span>{copy.description}</span>
        </CampaignPreviewEmpty>
      );
    }

    return (
      /* Never add allow-scripts: combined with allow-same-origin the frame can
         remove its own sandbox attribute. */
      <CampaignPreviewFrame
        title='Email preview'
        sandbox='allow-same-origin'
        onLoad={makePreviewReadOnly}
        srcDoc={`${campaignPreview.preview.html}${PREVIEW_DISABLE_LINKS}`}
      />
    );
  };

  return (
    <>
      {backLink}

      <CampaignDetailCard>
        <CampaignDetailTitle>
          {campaignTitle({subject: details?.subject, name})}
        </CampaignDetailTitle>
        <CampaignDetailPills>
          {statusLabel && (
            <CampaignDetailPill className={details?.statusLabel}>
              {/* The check SVG has the green baked in. */}
              {isActive && <img src={IconPillActive} alt='' />}
              {statusLabel}
            </CampaignDetailPill>
          )}
          <CampaignDetailPill>
            <img src={IconPillType} alt='' />
            {typeLabel}
          </CampaignDetailPill>
          <CampaignDetailPill>
            <img src={IconPillClock} alt='' />
            Last sent {formatCampaignDate(details?.lastSentAt)}
          </CampaignDetailPill>
          <CampaignDetailPill>
            <img src={IconPillDelivered} alt='' />
            {formatCount(tiles?.delivered)} delivered
          </CampaignDetailPill>
        </CampaignDetailPills>
      </CampaignDetailCard>

      <CampaignDetailCard className='tiles'>
        <CampaignTiles>
          <CampaignTile>
            <CampaignTileValue>{formatCount(tiles?.sent)}</CampaignTileValue>
            <CampaignTileName>Sent</CampaignTileName>
          </CampaignTile>
          <CampaignTile>
            <CampaignTileValue>
              {formatCount(tiles?.delivered)}
            </CampaignTileValue>
            <CampaignTileName>Delivered</CampaignTileName>
          </CampaignTile>
          <CampaignTile>
            <CampaignTileValue className='green'>
              {formatRate(tiles?.openRate)}
            </CampaignTileValue>
            <CampaignTileName>Open Rate</CampaignTileName>
          </CampaignTile>
          <CampaignTile>
            <CampaignTileValue>
              {formatRate(tiles?.clickRate)}
            </CampaignTileValue>
            <CampaignTileName>Click Rate</CampaignTileName>
          </CampaignTile>
        </CampaignTiles>
      </CampaignDetailCard>

      <CampaignDetailColumns>
        <CampaignDetailCard>
          <CampaignSectionTitle>Delivery funnel</CampaignSectionTitle>
          <CampaignFunnel>
            {funnel.map(({stage, count}) => (
              <CampaignFunnelStage key={stage}>
                <CampaignFunnelCount>{formatCount(count)}</CampaignFunnelCount>
                <CampaignFunnelTrack>
                  <CampaignFunnelFill
                    style={{
                      height:
                        funnelMax && count
                          ? `max(6px, ${(count / funnelMax) * 100}%)`
                          : 0
                    }}
                  />
                </CampaignFunnelTrack>
                <CampaignFunnelLabel>
                  {FUNNEL_LABELS[stage] || stage}
                </CampaignFunnelLabel>
              </CampaignFunnelStage>
            ))}
          </CampaignFunnel>
        </CampaignDetailCard>

        <CampaignDetailCard className='rows'>
          <CampaignSectionTitle>Campaign details</CampaignSectionTitle>
          <CampaignDetailRow>
            <CampaignDetailLabel>Subject line</CampaignDetailLabel>
            <CampaignDetailValue>
              {details?.subject || name || EMPTY_VALUE}
            </CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Status</CampaignDetailLabel>
            <CampaignDetailValue className={isActive ? 'green' : ''}>
              {statusLabel || EMPTY_VALUE}
            </CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Last sent</CampaignDetailLabel>
            <CampaignDetailValue>
              {formatCampaignDate(details?.lastSentAt)}
            </CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Total sent</CampaignDetailLabel>
            <CampaignDetailValue>
              {formatCount(tiles?.sent)}
            </CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Delivered</CampaignDetailLabel>
            <CampaignDetailValue>
              {formatCount(audience?.delivered)}
              {audience?.deliveredPct !== null &&
                audience?.deliveredPct !== undefined &&
                ` (${formatRateShort(audience.deliveredPct)})`}
            </CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Clicks</CampaignDetailLabel>
            <CampaignDetailValue>{formatCount(clicks)}</CampaignDetailValue>
          </CampaignDetailRow>
          <CampaignDetailRow>
            <CampaignDetailLabel>Orders attributed</CampaignDetailLabel>
            <CampaignDetailValue>
              {formatCount(tiles?.orders)}
            </CampaignDetailValue>
          </CampaignDetailRow>
        </CampaignDetailCard>
      </CampaignDetailColumns>

      <CampaignDetailCard>
        <CampaignSectionTitle>Email preview</CampaignSectionTitle>
        {renderPreview()}
      </CampaignDetailCard>
    </>
  );
};

CampaignDetail.propTypes = {
  campaignId: PropTypes.string.isRequired,
  sourceType: PropTypes.string,
  onBack: PropTypes.func.isRequired
};

CampaignDetail.defaultProps = {
  sourceType: null
};

export default CampaignDetail;
